import { EQUIPMENT_COUNTERS_ENDPOINT } from "../../../../../../consts";
import { useCRUD } from "../../../../../../api/crud";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { EquipmentCounter } from "../../../../../../types/equipments";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import { useTranslation } from "react-i18next";
import { useBreadcrumbs } from "../../../../../../components/ui/breadcrumps/breadcrumbsContext";
import {
  DetailHeader,
  DetailMetadata,
  DetailPage,
  DetailRow,
  DetailSection,
  UserDisplay,
} from "../../../../../../components/ui/detailView";
import { formatCounterProvider, formatCounterValue, formatDateTimeInline } from "../../../../../../utils";

export const EquipmentCounterDetail = () => {
  const { id, counterId } = useParams();
  const { t } = useTranslation();
  const { get } = useCRUD(EQUIPMENT_COUNTERS_ENDPOINT);
  const { setOverride } = useBreadcrumbs();
  const [counter, setCounter] = useState<EquipmentCounter | null>(null);

  useEffect(() => {
    const fetchCounter = async () => {
      if (counterId) {
        const response = await get(counterId);
        if (response.data) {
          setCounter(response.data);
          const counterLabel = `${response.data.kind_display} - ${formatDateTimeInline(response.data.read_at)}`;
          setOverride(`/equipment-section/equipments/${id}/counters/${counterId}`, counterLabel);
        }
      }
    };

    const handleCounterUpdated = () => {
      fetchCounter();
    };

    window.addEventListener("equipmentCounterUpdated", handleCounterUpdated);
    fetchCounter();
    return () => {
      window.removeEventListener("equipmentCounterUpdated", handleCounterUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counterId]);

  if (!counter) return <DetailPage loading />;

  return (
    <DetailPage>
      <DetailHeader
        icon={<SpeedOutlinedIcon />}
        title={`${counter.kind_display} - ${formatDateTimeInline(counter.read_at)}`}
        subtitle={`${t("Value")}: ${formatCounterValue(counter.value)}`}
      />

      <DetailSection title={t("Basic Information")}>
        <DetailRow label={t("Kind")} value={counter.kind_display || counter.kind} />
        <DetailRow
          label={t("Provider")}
          value={formatCounterProvider(
            counter.provider_display || counter.provider,
            counter.email_source_display,
          )}
        />
        <DetailRow label={t("Value")} value={formatCounterValue(counter.value)} />
        <DetailRow label={t("Read at")} value={formatDateTimeInline(counter.read_at)} />
        {counter.provided_by && (
          <DetailRow label={t("Provided by")}>
            <UserDisplay user={counter.provided_by} />
          </DetailRow>
        )}
      </DetailSection>

      <DetailMetadata createdAt={counter.created_at} updatedAt={counter.updated_at} />
    </DetailPage>
  );
};
